import { ol_Feature, ol_format, ol_source, ol_geom, ol_GeometryType } from "@omnia/exports/openlayers"
import { catchApiError, defineComponent, FieldsMap, FieldViewModel, IOmniaShell, LocaleString, map } from "../omnia"
import { validateSplit } from "../split/validation"
import { ICroppingSplitModel, IFieldsEditService, IFieldsService } from "../types"
import { EditLayer } from "./edit-layer"
import { GrassMarginCmd } from "./grassMarginCmd"
import { validateGeometries } from "./validation"

/// <reference path="edit-layer.ts" />
/// <reference path="validation.ts" />
/// <reference path="../split/validation.ts" />

export enum EditingStep {
    SelectMode = 0,
    EditInfieldMargin = 3,
    DrawMargin = 4,
}

export class GrassMarginEditorCtrl extends omnia.shell.Component {

    static create(): ng.IDirective {

        return defineComponent(GrassMarginEditorCtrl, "app/fields/grassMargin/grassMargin.html", {
            fieldsMap: "="
        });

    }


    public fieldsMap: FieldsMap;
    public cropping: FieldViewModel;

    public selectedMode: string;

    private _croppingFeature: ol_Feature;

    private _layer: EditLayer;

    private _step: EditingStep = EditingStep.SelectMode;

    private _marginWidth: number = 10
    private _invalidMargin: boolean = false

    public marginArea: number

    public selectedFeature: ol_Feature;

    static $inject = ["$scope", "OmniaFieldsService", "OmniaFieldsEditService", "OmniaMapTopologyService", "OmniaShell"];

    constructor(
        $scope: ng.IScope,
        private svc: IFieldsService,
        private editSvc: IFieldsEditService,
        private topo: omnia.map.IMapTopologyService,
        private shell: IOmniaShell,
        private measure = topo.measure) {

        super($scope, ["vm.fieldsMap"]);

        this._icon = "icon-split";
        this._title = "Draw a grass margin" as LocaleString;

    }


    get step(): EditingStep {
        return this._step;
    }

    get marginWidth(): number {
        return this._marginWidth;
    }

    set marginWidth(value: number) {
        this._marginWidth = value
        this.updateMarginWidth()
    }

    get invalidMargin(): boolean {
        return this._invalidMargin;
    }

    protected setup() {

        var cmd = <GrassMarginCmd>this.execArguments;

        if (!cmd) {
            this.cropping = null;
            return;
        }

        this.cropping = cmd.cropping;

        this.setupMapDisplayMode();

        this.editSvc.startEditing(true, this.cropping.cropping);

        this._step = EditingStep.SelectMode;

    }

    protected dispose() {

        this.cleanupMap();

    }

    cancel() {

        this.editSvc.stopEditing();

        this.cleanupMap();

        super.cancel();

    }

    getCroppingDescription(): string {
        if (this.cropping) return this.cropping.getDescription(false);
    }

    selectDrawingMode() {

        if (this.selectedMode == "infield") {
            this._step = EditingStep.EditInfieldMargin;
            this.setupMapEditMode();
        }

        if (this.selectedMode == "edge2") {
            this._step = EditingStep.DrawMargin;
            this.setupMarginInteraction();
        }

    }

    private updateMarginWidth() {

        this._invalidMargin = false;

        if (this._marginWidth < 1) {
            this._invalidMargin = true;
            return;
        }

        if (this._step == EditingStep.DrawMargin) {
            this._marginInteraction.refresh();
        }

        this.updateMarginArea()

    }


    hasMultipleFeatures(): boolean {
        return this._layer.featuresCount > 1;
    }

    canDeleteSelectedFeature(): boolean {

        return this.selectedFeature && this.hasMultipleFeatures();

    }

    removeSelectedFeature() {

        if (this.selectedFeature) {
            this._layer.removeFeature(this.selectedFeature);
            this.selectedFeature = null;
        }

    }

    private onNewFeatureAdded(feature: any): void {

        if (this._step == EditingStep.EditInfieldMargin) {
            this.selectedFeature = feature;
            this.$scope.$applyAsync();
        }

    }

    private onMapEditSelectionChanged(feature: any): boolean {

        this.selectedFeature = feature;
        this.$scope.$applyAsync();

        return true;

    }

    private startDrawingFeature(type: ol_GeometryType | "Box" | "Square") {

        var map = this.fieldsMap;

        this.fieldsMap.commitEdit();

        map.startDrawingNewFeature(this._layer, type);

    }

    private startPolygon() {
        this.startDrawingFeature("Polygon");
    }

    private startBox() {
        this.startDrawingFeature("Box");
    }

    private startSquare() {
        this.startDrawingFeature("Square");
    }

    private startCircle() {
        this.startDrawingFeature("Circle");
    }

    private setupMapEditMode() {

        var map = this.fieldsMap;

        map.autoEdit({
            layer: this._layer,
            watcher: f => this.onMapEditSelectionChanged(f)
        });

    }

    private setupMapDisplayMode() {

        var map = this.fieldsMap;

        this._layer = new EditLayer(f => this.onNewFeatureAdded(f));

        map.addLayer(this._layer);

        map.setSelectableLayers([this._layer]);

        map.synchronizeSelectionFromMap = false;
        map.enableMultipleSelection = false;

        var cropFeature = map.findFeatureWithData(this.cropping);

        if (cropFeature) {

            this._croppingFeature = cropFeature;
            map.viewExtent(cropFeature.getGeometry());

            map.fieldsLayer.highlight([this.cropping.fieldVm]);
            map.croppingLayer.setHighlightFeatures([cropFeature]);

        }

    }

    private cleanupMap() {

        var map = this.fieldsMap;

        map.cancelEdit();

        map.fieldsLayer.setHiddenFeatures(null);
        map.croppingLayer.setHiddenFeatures(null);

        map.autoEdit(null);

        map.removeLayer(this._layer);

        map.clearSelection();

        map.fieldsLayer.clearHighlight();
        map.croppingLayer.clearHighlight();

        map.autoEdit(null);
        map.synchronizeSelectionFromMap = true;

        map.resetDefaultLayers(true);

        if (this._marginInteractionWatcher) {
            this._marginInteractionWatcher();
            this._marginInteractionWatcher = null;
        }

        if (this._marginInteraction) {
            const olmap = map.getMap();
            olmap.removeInteraction(this._marginInteraction);
        }

    }


    public canCommit(): boolean {

        let step = this._step;

        switch (this.step) {

            case EditingStep.EditInfieldMargin:

                let map = this.fieldsMap;
                if (!this._layer.featuresCount) return false;

                return true;

            case EditingStep.DrawMargin:

                if (!this._marginInteraction) return false;
                if (this._invalidMargin) return false;
                return !!this._marginInteraction.selection;

            default:

                return false;

        }


    }

    public commit() {


        let outerGeometry = this._croppingFeature.getGeometry();

        let geometries: ol_geom.Geometry[];

        if (this._step == EditingStep.DrawMargin) {
            geometries = [this.getDrawMarginGeometry()];
        } else {
            this.fieldsMap.commitEdit();
            geometries = this._layer.getGeometries();
        }

        if (!validateGeometries(this.shell, this.topo, outerGeometry, geometries)) {
            return false;
        }

        validateSplit(this.cropping, this.shell, this.svc).then(ok => {

            if (ok) {

                const wkt = new ol_format.WKT();

                let splits = geometries.map(g => {

                    return {
                        GrassMargin: true,
                        Area: Math.round(this.measure.getArea(g) / 100) / 100,
                        BoundaryWKT: wkt.writeGeometry(g),
                    }

                });


                splits = splits.concat(<any>{
                    RetainCroppingInfo: true
                });

                const model: ICroppingSplitModel = {

                    HoldingId: this.svc.holdingId,
                    YearId: this.svc.yearId,
                    FieldId: this.cropping.fieldId,
                    CroppingId: this.cropping.croppingId,

                    DeleteLayers: ok == "delete",

                    Splits: splits

                }

                catchApiError(this.shell, () =>

                    this.svc.splitCropping(model).then(() => this.cancel())

                )

            }

        })

    }


    public fullBoundaryMargin: boolean = false;

    private _marginInteraction: omnia.map.DrawMarginInteraction;
    private _marginInteractionWatcher: Function;

    private setupMarginInteraction() {

        let source = new ol_source.Vector({
            features: [this._croppingFeature]
        });

        let interaction = new omnia.map.DrawMarginInteraction(source);

        const olmap = this.fieldsMap.map.getMap();
        olmap.addInteraction(interaction);

        this._marginInteraction = interaction;

        this._marginInteractionWatcher = omnia.map.observeOpenlayersObject(interaction, ["change", "move"], () => this.handleDrawMarginChange());

        interaction.setActive(true);

        this._marginInteraction.geometryFunction = line => this.bufferMargin(line, 0);

    }

    private getDrawMarginGeometry(): ol_geom.Geometry {

        if (!this._marginInteraction) return null;

        let line = this._marginInteraction.selection;
        if (!line) return null;

        let geom = this.bufferMargin(line, 0.01);

        return geom;

    }

    private bufferMargin(line: ol_geom.LineString, buffer: number): ol_geom.Geometry {

        let distance = this.marginWidth;
        if (distance <= 0) return line;

        distance = this.measure.getProjectedDistance(line.getFirstCoordinate(), distance);

        let r = this.topo.buffer2(line, distance);

        let geom = r.getOpenLayersGeometry();

        r = this.topo.intersection(geom, this._croppingFeature.getGeometry(), true);

        if (buffer) {
            r = this.topo.buffer(r, buffer);
        }

        geom = r.getOpenLayersGeometry();

        return geom;

    }

    private handleDrawMarginChange() {
        this.updateMarginArea()
        this.$scope.$applyAsync()
    }

    private updateMarginArea() {

        const geom = this._marginInteraction.getCurrentSelectionGeometry()
        const area = geom ? this.topo.measure.getArea(geom) : null

        this.marginArea = Math.round(area / 100) / 100

    }

    switchMarginDrawingDirection() {

        if (this._marginInteraction) {
            this._marginInteraction.switchDirection();
        }

    }

    updateMarginType() {

        if (this._marginInteraction) {
            this._marginInteraction.setFullBoundaryMargin(this.fullBoundaryMargin)
        }
    }

}
